import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api, apiUpload, fileUrl } from "@/lib/api";
import { swallow } from "@/lib/log";
import {
  Send, Sparkles, Trash2, Camera, Loader2, Video, Folder, FolderPlus,
  X, Check, ChevronDown, CalendarDays, MoreVertical, Mic,
} from "lucide-react";
import ActionCard from "@/components/ActionCard";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import VoiceInput from "@/components/VoiceInput";
import ChatRecentStrip from "@/components/ChatRecentStrip";

function fmtBubbleTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const hh = d.getHours();
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ampm = hh >= 12 ? "PM" : "AM";
    const h12 = ((hh + 11) % 12) + 1;
    return `${h12}:${mm} ${ampm}`;
  } catch { return ""; }
}

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollerRef = useRef(null);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  // Pending file waiting for folder name
  const [pendingFile, setPendingFile] = useState(null); // { file, kind: 'photo'|'video', previewUrl }
  const [folderChoice, setFolderChoice] = useState({ mode: "none", id: "", name: "" });
  const [customName, setCustomName] = useState("");
  const [folders, setFolders] = useState([]);
  const [deleteRangeOpen, setDeleteRangeOpen] = useState(false);
  const [deleteCustom, setDeleteCustom] = useState({ from: "", to: "" });

  const scrollBottom = (behavior = "auto") => {
    // Two RAFs so we scroll AFTER layout has stabilised.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = scrollerRef.current;
        if (el) {
          el.scrollTo({ top: el.scrollHeight, behavior });
        }
      });
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/chat/history");
        setMessages(data || []);
        // Wait for messages to render, then jump straight to the bottom (no smooth animation on first load)
        scrollBottom("auto");
      } catch (e) { swallow("Chat/history")(e); }
    })();
  }, []);

  // Web Share Target + Voice-first FAB (audits #4 + #5)
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const shared = sp.get("share");
    const voice = sp.get("voice");
    let touched = false;
    if (shared) {
      setInput(shared);
      toast.success("Shared content added — tap send when ready");
      touched = true;
    }
    if (voice === "1") {
      // Fire the same voice event our listener handles, one tick later so the listener is bound
      setTimeout(() => window.dispatchEvent(new CustomEvent("impo:chat-voice")), 200);
      touched = true;
    }
    if (touched) {
      const next = new URLSearchParams(sp);
      next.delete("share"); next.delete("voice");
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  // Voice-first FAB — center button on the HOME (Chat) route starts dictation.
  useEffect(() => {
    const startVoice = () => {
      // Prefer SpeechRecognition where available
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) {
        toast.error("Voice not supported on this device");
        return;
      }
      try {
        const rec = new SR();
        rec.lang = navigator.language || "en-US";
        rec.interimResults = true;
        rec.continuous = false;
        let final = "";
        toast.message("Listening…", { description: "Speak now — Impo will transcribe.", duration: 4000, id: "voice-listening" });
        rec.onresult = (e) => {
          let interim = "";
          for (let i = e.resultIndex; i < e.results.length; i++) {
            const t = e.results[i][0].transcript;
            if (e.results[i].isFinal) final += t; else interim += t;
          }
          setInput((prev) => (prev ? (prev + " " + interim || final) : (final || interim)));
        };
        rec.onerror = (ev) => {
          toast.dismiss("voice-listening");
          toast.error("Couldn't hear you — try again");
        };
        rec.onend = () => {
          toast.dismiss("voice-listening");
          if (final) toast.success("Got it — tap send to save");
        };
        rec.start();
      } catch (err) {
        toast.error("Voice failed to start");
      }
    };
    window.addEventListener("impo:chat-voice", startVoice);
    return () => window.removeEventListener("impo:chat-voice", startVoice);
  }, []);

  const loadFolders = async () => {
    try { const { data } = await api.get("/folders"); setFolders(data || []); }
    catch (e) { swallow("Chat/folders")(e); }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setSending(true);
    const optimistic = { id: `tmp-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setInput("");
    scrollBottom();
    try {
      const { data } = await api.post("/chat/send", { message: text });
      setMessages((m) => {
        const filtered = m.filter((x) => x.id !== optimistic.id);
        return [...filtered, data.user_message, data.assistant_message];
      });
      scrollBottom();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  const runDelete = async (params) => {
    try {
      const query = new URLSearchParams(params).toString();
      const { data } = await api.delete(`/chat/history${query ? "?" + query : ""}`);
      // Refetch messages
      const { data: hist } = await api.get("/chat/history");
      setMessages(hist || []);
      toast.success(`Deleted ${data.deleted || 0} messages`);
      setDeleteRangeOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const onFilePick = (kind) => (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const isImage = f.type.startsWith("image/");
    const isVideo = f.type.startsWith("video/");
    if (kind === "photo" && !isImage) { toast.error("Please pick an image"); return; }
    if (kind === "video" && !isVideo) { toast.error("Please pick a video"); return; }
    const limit = kind === "video" ? 200 * 1024 * 1024 : 15 * 1024 * 1024;
    if (f.size > limit) { toast.error(`File too large (max ${kind === "video" ? "200" : "15"} MB)`); return; }
    // Load existing folders in the background so the picker has options
    loadFolders();
    setFolderChoice({ mode: "none", id: "", name: "" });
    setCustomName("");
    setPendingFile({ file: f, kind, previewUrl: isImage ? URL.createObjectURL(f) : null });
  };

  const cancelPending = () => {
    if (pendingFile?.previewUrl) { try { URL.revokeObjectURL(pendingFile.previewUrl); } catch { /* noop */ } }
    setPendingFile(null);
  };

  const commitUpload = async () => {
    if (!pendingFile) return;
    const { file, kind, previewUrl } = pendingFile;
    // Determine folder
    let folder_id = null;
    let folder_name = null;
    if (folderChoice.mode === "existing" && folderChoice.id) folder_id = folderChoice.id;
    else if (folderChoice.mode === "new" && folderChoice.name.trim()) folder_name = folderChoice.name.trim();

    // Optimistic bubbles
    const tmpUserId = `tmp-user-${Date.now()}`;
    const tmpAssistantId = `tmp-a-${Date.now()}`;
    const folderLabel = folder_name || (folders.find((f) => f.id === folder_id)?.name) || null;
    setMessages((m) => [
      ...m,
      {
        id: tmpUserId, role: "user",
        content: kind === "video" ? "🎥 Attached a video" : "📸 Attached a photo",
        attachment_preview: previewUrl,
        created_at: new Date().toISOString(),
      },
      {
        id: tmpAssistantId, role: "assistant",
        content: `Saving${folderLabel ? ` to "${folderLabel}"` : ""}…`,
        pending: true,
        created_at: new Date().toISOString(),
      },
    ]);
    setPendingFile(null);
    scrollBottom();

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Priority: user-typed customName > folder label > friendly default.
      const assetTitle = (customName && customName.trim())
        ? customName.trim()
        : folderLabel
          ? folderLabel
          : (kind === "video" ? "Video" : "Photo") + " " + new Date().toLocaleDateString();
      fd.append("title", assetTitle);
      if (folder_id) fd.append("folder_id", folder_id);
      if (folder_name) fd.append("folder_name", folder_name);
      const endpoint = kind === "video" ? "/videos" : "/photos";
      const data = await apiUpload(endpoint, fd);
      setMessages((m) => m.map((msg) => {
        if (msg.id !== tmpAssistantId) return msg;
        return {
          ...msg,
          content: folderLabel ? `Saved to "${folderLabel}"` : "Saved to your Media",
          pending: false,
          action: {
            type: `${kind}_saved`,
            data: { id: data.id, title: data.title, file_url: data.file_url, folder_label: folderLabel },
          },
        };
      }));
      toast.success(`${kind === "video" ? "Video" : "Photo"} saved${folderLabel ? ` · ${folderLabel}` : ""}`);
    } catch (err) {
      setMessages((m) => m.map((msg) => (msg.id === tmpAssistantId
        ? { ...msg, content: `Couldn't save that ${kind}. Try again?`, pending: false }
        : msg)));
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-[calc(100dvh-76px)] bg-[hsl(var(--ivory))] text-[hsl(var(--ivory-foreground))]">
      <TopBar
        title="Impo"
        subtitle="Say anything — I'll remember it"
        showSearch
        showHome={false}
        right={
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-muted"
                data-testid="chat-more-menu-trigger"
                aria-label="More options"
              >
                <MoreVertical className="h-4 w-4 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-1" data-testid="chat-more-menu-content">
              <button
                onClick={() => runDelete({ preset: "day" })}
                className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted"
                data-testid="chat-delete-day"
              >Clear last day</button>
              <button
                onClick={() => runDelete({ preset: "week" })}
                className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted"
                data-testid="chat-delete-week"
              >Clear last week</button>
              <button
                onClick={() => runDelete({ preset: "month" })}
                className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted"
                data-testid="chat-delete-month"
              >Clear last month</button>
              <button
                onClick={() => setDeleteRangeOpen(true)}
                className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted flex items-center gap-2"
                data-testid="chat-delete-range-open"
              >
                <CalendarDays className="h-3.5 w-3.5" /> Custom date range…
              </button>
              <div className="h-px bg-border my-1" />
              <button
                onClick={() => { if (window.confirm("Delete ALL chat history? This can't be undone.")) runDelete({ preset: "all" }); }}
                className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-destructive/10 text-destructive flex items-center gap-2"
                data-testid="chat-delete-all"
              >
                <Trash2 className="h-3.5 w-3.5" /> Clear everything
              </button>
            </PopoverContent>
          </Popover>
        }
      />

      <div ref={scrollerRef} className="flex-1 px-4 py-4 space-y-3 overflow-y-auto" data-testid="chat-messages-list">
        <ChatRecentStrip />
        {messages.length === 0 && (
          <div className="px-2 pt-8">
            <div className="flex items-center gap-2 text-primary">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">Try saying</span>
            </div>
            <div className="mt-2 grid gap-2">
              {[
                "Save a note: buy milk and eggs tomorrow",
                "Add to my journal: today was long but I finally got the report done.",
                "Change my HDFC NetBanking password to NewSecure2029!",
                "Where is my passport?",
              ].map((s) => (
                <button
                  key={s}
                  className="text-left text-sm rounded-2xl border border-border bg-card px-4 py-3 hover:bg-[hsl(var(--accent))] transition"
                  onClick={() => send(s)}
                  data-testid="chat-example-button"
                >{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <MessageBubble key={m.id} msg={m} />
        ))}

        {sending && (
          <div className="flex justify-start">
            <div className="max-w-[82%] rounded-2xl bg-secondary text-foreground px-4 py-3 text-sm border border-border">
              <span className="impo-dot" /><span className="impo-dot" /><span className="impo-dot" />
            </div>
          </div>
        )}
        <div className="h-2" />
      </div>

      <div className="sticky bottom-0 bg-background border-t border-border px-3 pt-3 pb-[calc(env(safe-area-inset-bottom)+8px)]">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
          <input ref={photoInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFilePick("photo")} data-testid="chat-camera-input" />
          <input ref={videoInputRef} type="file" accept="video/*" capture="environment" className="hidden" onChange={onFilePick("video")} data-testid="chat-video-input" />
          <div className="flex gap-1 shrink-0">
            <button
              type="button"
              onClick={() => !uploading && photoInputRef.current?.click()}
              disabled={uploading}
              className="h-11 w-11 rounded-full border border-border bg-card hover:bg-[hsl(var(--accent))] flex items-center justify-center disabled:opacity-60"
              aria-label="Capture photo"
              data-testid="chat-camera-button"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4 text-muted-foreground" />}
            </button>
            <button
              type="button"
              onClick={() => !uploading && videoInputRef.current?.click()}
              disabled={uploading}
              className="h-11 w-11 rounded-full border border-border bg-card hover:bg-[hsl(var(--accent))] flex items-center justify-center disabled:opacity-60"
              aria-label="Record video"
              data-testid="chat-video-button"
            >
              <Video className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder="Message Impo…"
            rows={1}
            className="flex-1 min-h-[44px] max-h-[140px] resize-none rounded-2xl bg-card border border-border px-3 py-3 text-sm leading-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid="chat-composer-textarea"
          />
          <VoiceInput size="lg" onTranscript={(t) => setInput(t)} testId="chat-voice-input" />
          <Button type="submit" size="icon" className="h-11 w-11 rounded-full" disabled={!input.trim() || sending} data-testid="chat-composer-send-button">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>

      {/* ── Folder prompt dialog ─────────────────────────────── */}
      <FolderPromptDialog
        open={!!pendingFile}
        pending={pendingFile}
        folders={folders}
        choice={folderChoice}
        setChoice={setFolderChoice}
        customName={customName}
        setCustomName={setCustomName}
        onCancel={cancelPending}
        onSave={commitUpload}
      />

      {/* ── Custom date-range delete dialog ──────────────────── */}
      <Dialog open={deleteRangeOpen} onOpenChange={setDeleteRangeOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Clear chat by date range</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">From</Label>
              <Input
                type="datetime-local"
                value={deleteCustom.from}
                onChange={(e) => setDeleteCustom({ ...deleteCustom, from: e.target.value })}
                data-testid="chat-delete-range-from"
              />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input
                type="datetime-local"
                value={deleteCustom.to}
                onChange={(e) => setDeleteCustom({ ...deleteCustom, to: e.target.value })}
                data-testid="chat-delete-range-to"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">Both fields are optional. Leaving one blank means "open-ended" on that side.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteRangeOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                const params = {};
                if (deleteCustom.from) params.from_date = new Date(deleteCustom.from).toISOString();
                if (deleteCustom.to) params.to_date = new Date(deleteCustom.to).toISOString();
                if (!params.from_date && !params.to_date) {
                  toast.error("Pick at least one date");
                  return;
                }
                runDelete(params);
              }}
              data-testid="chat-delete-range-confirm"
            >
              Clear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FolderPromptDialog({ open, pending, folders, choice, setChoice, customName, setCustomName, onCancel, onSave }) {
  const kind = pending?.kind || "photo";
  const relevantFolders = folders.filter((f) =>
    !f.scope
    || f.scope === (kind === "video" ? "videos" : kind === "photo" ? "photos" : "documents")
  );
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            Save this {kind} to…
          </DialogTitle>
        </DialogHeader>

        {pending?.previewUrl && (
          <div className="rounded-2xl overflow-hidden border border-border max-h-56">
            <img src={pending.previewUrl} alt="preview" className="w-full max-h-56 object-contain bg-muted" />
          </div>
        )}
        {pending && !pending.previewUrl && (
          <div className="rounded-2xl bg-muted p-4 text-sm text-muted-foreground">
            {pending.file?.name} · {(pending.file?.size / 1024 / 1024).toFixed(1)} MB
          </div>
        )}

        {/* Primary field: what to call this asset */}
        <div>
          <Label className="text-xs">Save as (name)</Label>
          <Input
            autoFocus
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder={
              kind === "video"
                ? "e.g. Goa trip 2025, School annual day"
                : "e.g. Aadhaar front, Beach sunset"
            }
            data-testid="chat-upload-name-input"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            This name is used everywhere — Media list, chat search, deep links.
          </p>
        </div>

        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 mb-0 px-1">Optional folder</div>
          {/* Option A: Unfiled */}
          <button
            type="button"
            onClick={() => setChoice({ mode: "none", id: "", name: "" })}
            className={`w-full rounded-xl border p-3 flex items-center gap-3 text-left transition ${
              choice.mode === "none" ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-card"
            }`}
            data-testid="chat-folder-option-none"
          >
            <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
              <Folder className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium">Just save it (no folder)</div>
              <div className="text-[11px] text-muted-foreground">Goes straight to Media, unfiled.</div>
            </div>
            {choice.mode === "none" && <Check className="h-4 w-4 text-primary" />}
          </button>

          {/* Option B: Existing folder */}
          {relevantFolders.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 mb-1 px-1">Save to existing folder</div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {relevantFolders.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setChoice({ mode: "existing", id: f.id, name: f.name })}
                    className={`w-full rounded-xl border p-2.5 flex items-center gap-3 text-left transition ${
                      choice.mode === "existing" && choice.id === f.id
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border bg-card"
                    }`}
                    data-testid={`chat-folder-option-existing-${f.id}`}
                  >
                    <div className="h-8 w-8 rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center">
                      <Folder className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{f.name}</div>
                      <div className="text-[10px] text-muted-foreground">{f.counts?.total || 0} item(s)</div>
                    </div>
                    {choice.mode === "existing" && choice.id === f.id && <Check className="h-4 w-4 text-primary" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Option C: New folder */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1 mb-1 px-1">Or create a new folder</div>
            <div
              className={`rounded-xl border p-3 flex items-center gap-3 transition ${
                choice.mode === "new" ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-card"
              }`}
            >
              <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <FolderPlus className="h-4 w-4" />
              </div>
              <Input
                placeholder="e.g. Trip to Goa, School docs, Birthday 2025"
                value={choice.mode === "new" ? choice.name : ""}
                onFocus={() => setChoice({ mode: "new", id: "", name: choice.name })}
                onChange={(e) => setChoice({ mode: "new", id: "", name: e.target.value })}
                className="flex-1 h-9"
                data-testid="chat-folder-new-name-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-row gap-2 justify-end">
          <Button variant="outline" onClick={onCancel} data-testid="chat-folder-cancel-btn">
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={choice.mode === "new" && !choice.name.trim()}
            data-testid="chat-folder-save-btn"
          >
            <Check className="h-4 w-4 mr-1" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  const time = fmtBubbleTime(msg.created_at);

  // Suppress the AI's text bubble when a self-descriptive action card is already
  // showing the same info (e.g. a "Journal entry saved" text next to a
  // "Journal entry saved" card looks like a duplicate). We keep bubbles for
  // conversational actions like retrieve/question/request_upload.
  const actionType = msg.action?.type || "";
  const redundantWithCard = !isUser && !msg.pending && !!msg.action && (
    actionType.endsWith("_saved") ||
    actionType.endsWith("_updated") ||
    actionType.endsWith("_deleted")
  );
  const showBubble = !!msg.content && !redundantWithCard;

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} impo-in`}>
      <div className="max-w-[86%] space-y-2">
        {msg.attachment_preview && (
          <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
            <img
              src={msg.attachment_preview}
              alt="attachment"
              className="max-h-48 rounded-2xl border border-border object-cover shadow-[0_10px_30px_rgba(15,23,42,0.08)]"
              data-testid="chat-attachment-preview"
            />
          </div>
        )}
        {showBubble && (
          <div
            className={
              isUser
                ? "relative rounded-2xl bg-primary text-primary-foreground pl-4 pr-14 py-3 text-sm leading-6 shadow-[0_10px_30px_rgba(15,23,42,0.08)]"
                : "relative rounded-2xl bg-secondary text-foreground pl-4 pr-14 py-3 text-sm leading-6 border border-border"
            }
            data-testid={isUser ? "chat-user-message-bubble" : "chat-assistant-message-bubble"}
          >
            {msg.pending ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> {msg.content}
              </span>
            ) : msg.content}
            {time && (
              <span
                className={`absolute bottom-1 right-2.5 text-[10px] leading-none tabular-nums pointer-events-none ${
                  isUser ? "text-primary-foreground/70" : "text-muted-foreground"
                }`}
                data-testid="chat-bubble-timestamp"
              >
                {time}
              </span>
            )}
          </div>
        )}
        {msg.action && (msg.action.type === "photo_saved" || msg.action.type === "video_saved") ? (
          <div className="rounded-2xl border border-border bg-card p-3 flex items-center gap-3" data-testid={`chat-action-card-${msg.action.type}`}>
            {msg.action.data?.file_url && msg.action.type === "photo_saved" && (
              <img
                src={fileUrl(msg.action.data.file_url)}
                alt=""
                className="h-14 w-14 rounded-xl object-cover border border-border"
              />
            )}
            {msg.action.type === "video_saved" && (
              <div className="h-14 w-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <Video className="h-5 w-5" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                {msg.action.type === "video_saved" ? "Video saved" : "Photo saved"}
              </div>
              <div className="text-sm font-medium truncate">{msg.action.data?.title || (msg.action.type === "video_saved" ? "Video" : "Photo")}</div>
              {msg.action.data?.folder_label && (
                <div className="text-[10px] text-primary flex items-center gap-1 mt-0.5">
                  <Folder className="h-3 w-3" /> {msg.action.data.folder_label}
                </div>
              )}
              <a href="/app/media" className="mt-1 text-xs font-medium text-primary inline-flex items-center gap-1" data-testid="chat-action-open-media">
                Open Media
              </a>
            </div>
          </div>
        ) : msg.action ? (
          <ActionCard action={msg.action} />
        ) : null}
      </div>
    </div>
  );
}
